import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@gapartners/ui';
import type { DirectConnectLinkSectionProps } from './types';

export function DirectConnectLinkSection({
  t,
  companyQuery,
  setCompanyQuery,
  searching,
  linking,
  searchSucceeded,
  results,
  linkError,
  onSearch,
  onLink,
}: DirectConnectLinkSectionProps) {
  return (
    <>
      <Typography variant="body2" color="text.secondary">
        {t('components.contactCenter.directConnectCompanySubtitle')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          size="small"
          fullWidth
          value={companyQuery}
          onChange={e => setCompanyQuery(e.target.value)}
          placeholder={t(
            'components.contactCenter.directConnectCompanySearchPlaceholder'
          )}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSearch();
            }
          }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={onSearch}
          disabled={searching || !companyQuery.trim()}
          sx={{ flexShrink: 0, mt: 0.5 }}
        >
          {searching ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            t('components.contactCenter.directConnectSearch')
          )}
        </Button>
      </Box>

      {results.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('components.contactCenter.directConnectResults')}
          </Typography>
          {results.map(row => (
            <Box
              key={row.workspaceId}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                py: 0.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" color="text.primary">
                {row.name}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={linking}
                onClick={() => onLink(row.workspaceId, row.name)}
              >
                {linking
                  ? t('components.contactCenter.directConnectLinking')
                  : t('components.contactCenter.directConnectLink')}
              </Button>
            </Box>
          ))}
        </Box>
      )}

      {searchSucceeded &&
        companyQuery.trim() &&
        !searching &&
        results.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('components.contactCenter.directConnectNoResults')}
          </Typography>
        )}

      {linkError && (
        <Typography variant="body2" color="error.main">
          {linkError}
        </Typography>
      )}
    </>
  );
}
